import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import {
  LayoutDashboard, MessageSquare, FolderKanban,
  Sliders, Settings, LogOut, CreditCard, Shield
} from 'lucide-react'

const NAV = [
  { to: '/dashboard/overview',      icon: LayoutDashboard, label: 'Overview' },
  { to: '/dashboard/feedback',      icon: MessageSquare,   label: 'Feedback' },
  { to: '/dashboard/projects',      icon: FolderKanban,    label: 'Projects' },
  { to: '/dashboard/form-builder',  icon: Sliders,         label: 'Form Builder' },
  { to: '/dashboard/settings',      icon: Settings,        label: 'Settings' },
]

const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL

function getPageTitle(pathname: string): string {
  if (pathname.includes('admin')) return 'God Mode'
  if (pathname.includes('overview')) return 'Overview'
  if (pathname.includes('feedback')) return 'Feedback'
  if (pathname.includes('projects')) return 'Projects'
  if (pathname.includes('form-builder')) return 'Form Builder'
  if (pathname.includes('settings')) return 'Settings'
  if (pathname.includes('billing')) return 'Billing'
  if (pathname.includes('setup')) return 'Install SDK'
  return 'Dashboard'
}

export function Layout() {
  const { user, logout } = useAuth()
  const nav = useNavigate()
  const location = useLocation()
  const pageTitle = getPageTitle(location.pathname)

  async function handleLogout() {
    await logout()
    nav('/login')
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f7f9fb', color: '#191c1e' }}>
      {/* Sidebar */}
      <aside style={{
        width: 240, minHeight: '100vh', background: '#f2f4f6',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50,
      }}>
        {/* Logo */}
        <div style={{ padding: '24px 20px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: '#3525cd',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
            }}>💬</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#3525cd', fontFamily: 'Manrope, sans-serif', lineHeight: 1.2 }}>FeedbackKit</div>
              <div style={{ fontSize: 9, color: '#777587', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>Productivity Workspace</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 10px' }}>
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 12px', borderRadius: 8, marginBottom: 2,
              color: isActive ? '#3525cd' : '#464555',
              background: isActive ? '#ffffff' : 'transparent',
              textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 700 : 500,
              boxShadow: isActive ? '0 1px 4px rgba(25,28,30,0.08)' : 'none',
              transition: 'all 0.15s',
            })}>
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Admin link — only visible to the platform owner */}
        {ADMIN_EMAIL && user?.email === ADMIN_EMAIL && (
          <div style={{ padding: '0 10px 4px' }}>
            <NavLink to="/dashboard/admin" style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 12px', borderRadius: 8,
              color: isActive ? '#3525cd' : '#464555',
              background: isActive ? '#ffffff' : 'rgba(53,37,205,0.05)',
              textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 700 : 600,
              boxShadow: isActive ? '0 1px 4px rgba(25,28,30,0.08)' : 'none',
              border: '1px dashed rgba(53,37,205,0.25)',
            })}>
              <Shield size={16} />
              God Mode
            </NavLink>
          </div>
        )}

        {/* Bottom */}
        <div style={{ padding: '10px 10px 20px' }}>
          {/* Upgrade card */}
          <div style={{ padding: '14px', background: 'rgba(53,37,205,0.06)', borderRadius: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#3525cd', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {user?.plan === 'free' ? 'Free Plan' : user?.plan === 'indie' ? 'Indie Plan' : 'Studio Plan'}
            </div>
            <div style={{ fontSize: 11, color: '#464555', lineHeight: 1.5, marginBottom: 10 }}>
              {user?.plan === 'free' ? 'Unlock advanced analytics and custom branding.' : 'You have access to all features.'}
            </div>
            {user?.plan === 'free' && (
              <NavLink to="/dashboard/billing" style={{
                display: 'block', textAlign: 'center', padding: '7px 0',
                background: '#3525cd', color: '#fff', borderRadius: 8,
                fontSize: 12, fontWeight: 700, textDecoration: 'none',
              }}>Upgrade Plan</NavLink>
            )}
          </div>

          <NavLink to="/dashboard/billing" style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 12px', borderRadius: 8, marginBottom: 2,
            color: isActive ? '#3525cd' : '#464555',
            background: isActive ? '#ffffff' : 'transparent',
            textDecoration: 'none', fontSize: 14, fontWeight: 500,
            boxShadow: isActive ? '0 1px 4px rgba(25,28,30,0.08)' : 'none',
          })}>
            <CreditCard size={16} /> Billing
          </NavLink>

          {/* User row */}
          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: 'rgba(53,37,205,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#3525cd', flexShrink: 0,
            }}>
              {user?.name?.[0]?.toUpperCase() ?? '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', color: '#191c1e' }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: '#777587', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{user?.email}</div>
            </div>
            <button onClick={handleLogout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#777587', padding: 4, borderRadius: 6 }} title="Logout">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Top Navbar */}
      <header style={{
        position: 'fixed', top: 0, right: 0, left: 240, height: 64, zIndex: 40,
        background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 1px 0 rgba(25,28,30,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 32px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#191c1e', margin: 0, fontFamily: 'Manrope, sans-serif' }}>{pageTitle}</h2>
        </div>
      </header>

      {/* Main */}
      <main style={{ flex: 1, marginLeft: 240, paddingTop: 64, minHeight: '100vh' }}>
        <Outlet />
      </main>
    </div>
  )
}
